/**
 * The joogoos mark: the four o's in j-oo-g-oo-s, drawn as eyes, with the first mark's flow.
 *
 * The rings are tangent at their midlines and the band is heavy, two thirds of the mid radius, so
 * two neighbouring bands fuse where they overlap. What makes four rings one creature is the flow:
 * a ring lies on top along its lower-right arc and slides under everywhere else. So the top of
 * every pair fuses with no seam, top-left is over both its neighbours, top-right and bottom-left
 * are each over bottom-right, and bottom-right is under everything. Where a ring passes under, a
 * gap a fifth of the radius wide runs just outside the ring above, wide enough to reach into the
 * next hole; gap and hole are the same colour, so the top-right and bottom-left eyes become commas.
 * Three tails draw all of it. Measured from the original (outer 200, hole 103, centres 302 apart,
 * tails 40, on 1000), redrawn 2026-09-12.
 *
 * The eyes are the old eyes too: the hole is the pupil, and one small light sits up-left in each,
 * all four looking the same way.
 *
 * Three grounds, one drawing. Only what sits behind it changes:
 *
 *   cream  coral tile, cream rings, cream lights — the default, and what both the app and the site sign with
 *   coral  coral tile, ink rings, ink lights     — when the mark has to carry a hard edge
 *   ink    ink tile, cream rings, coral lights   — on a dark surface, where the coral tile would glare
 *
 * site/src/components/Logo.astro is the same drawing in the site's own orange identity (docs/15).
 *
 * `thinking` is the only state the mark has, and it is the weave (chosen 2026-09-12): the rings
 * stay white and one transparent snake, the tile's colour showing through them, three quarters of
 * a ring long and tapering to its tail, runs through all four loops on a single closed road that
 * switches ring where two touch, clockwise round the square, once round each ring on average (route
 * changed 2026-10-31): top-left, top-right, bottom-right, bottom-left. The tails and the eyes stay
 * where they are, so the snake passes under the gaps and through the eyes. It stays shorter than a
 * ring on purpose: longer, it covers the whole ring and overlaps itself, and for that stretch nothing
 * seems to move. Nothing else moves, and the tile never does. The mark is the app's only spinner, and
 * inside a `group` it also weaves under the pointer. Anyone who asked their system for less motion
 * keeps the resting mark instead.
 *
 * public/favicon.svg is the same drawing cut for a browser tab, where 16px is all there is: wider
 * tails and bigger lights. Inside the app only the working indicator draws it that small, at 18px.
 */
import type { CSSProperties } from "react";

type Tone = "ink" | "coral" | "cream";

const TONES: Record<Tone, { tile: string; ring: string; light: string }> = {
  ink: { tile: "var(--color-ink)", ring: "var(--color-page)", light: "var(--color-coral)" },
  coral: { tile: "var(--color-coral)", ring: "var(--color-ink)", light: "var(--color-ink)" },
  cream: { tile: "var(--color-coral)", ring: "var(--color-page)", light: "var(--color-page)" },
};

/** Centres 11.6 apart with a mid radius of 5.8: adjacent rings are tangent at their midlines. */
const AXIS = [12.2, 23.8] as const;
const EYES = AXIS.flatMap((cy) => AXIS.map((cx) => [cx, cy] as const));
const MID = 5.8;
const BAND = 3.7;
const GAP = 1.4;
const GLANCE = 1.85;
const [L, R] = AXIS;

const rad = (deg: number) => (deg * Math.PI) / 180;
const on = (cx: number, cy: number, r: number, deg: number) => `${(cx + r * Math.cos(rad(deg))).toFixed(2)} ${(cy + r * Math.sin(rad(deg))).toFixed(2)}`;

/** A tail runs along the circle just outside a ring's edge, GAP wide, from one angle to another. */
const TAIL_R = MID + BAND / 2 + GAP / 2;
const tail = (cx: number, cy: number, from: number, to: number) => `M ${on(cx, cy, TAIL_R, from)} A ${TAIL_R} ${TAIL_R} 0 0 1 ${on(cx, cy, TAIL_R, to)}`;
const TAILS = [
  tail(L, L, 0, 140), // top-left: out of the top-right hole, through the middle and the bottom-left hole, out at the left notch
  tail(R, L, 40, 90), // top-right: from the right notch into the bottom-right hole
  tail(L, R, 0, 50), // bottom-left: from the bottom notch into the bottom-right hole
];

/** The snake's road: arcs along a ring's midline, in pieces of at most a half turn. */
function arcs(cx: number, cy: number, from: number, sweep: number, clockwise: boolean): string {
  let d = "";
  for (let a = from, left = sweep; left > 0; ) {
    const step = Math.min(left, 180);
    const b = a + (clockwise ? step : -step);
    d += ` A ${MID} ${MID} 0 0 ${clockwise ? 1 : 0} ${on(cx, cy, MID, b)}`;
    a = b;
    left -= step;
  }
  return d;
}
/** Clockwise round the square: top-left 450° anticlockwise, top-right 270° clockwise, bottom-right 450° anticlockwise, bottom-left 270° clockwise. Four rings' worth. */
const ROAD = `M ${on(L, L, MID, 90)}${arcs(L, L, 90, 450, false)}${arcs(R, L, 180, 270, true)}${arcs(R, R, -90, 450, false)}${arcs(L, R, 0, 270, true)} Z`;
const ROAD_LENGTH = 4 * 2 * Math.PI * MID; // 145.77, which styles.css also knows as the slither's distance
/** The snake, tail first: strokes of the same road with their heads level, each shorter and wider than the last, the head a shade wider than a band so it cuts clean through. --back is the length, which is what keeps the heads level. */
const SNAKE = [[28, 1.3], [21, 2.1], [14, 2.9], [7, BAND + 0.3]] as const;

export function Logo({ size = 32, thinking = false, tone = "cream", className = "" }: { size?: number; thinking?: boolean; tone?: Tone; className?: string }) {
  const { tile, ring, light } = TONES[tone];
  return (
    <svg width={size} height={size} viewBox="0 0 36 36" className={className} role="img" aria-label="joogoos" focusable="false">
      <rect width="36" height="36" rx="10" fill={tile} />
      <g fill="none" stroke={ring} strokeWidth={BAND}>
        {EYES.map(([cx, cy]) => (
          <circle key={`r${cx},${cy}`} cx={cx} cy={cy} r={MID} />
        ))}
      </g>
      <g fill="none" stroke={tile} strokeLinecap="round" className={thinking ? "motion-reduce:hidden" : "hidden motion-safe:group-hover:block motion-safe:group-focus-visible:block"}>
        {SNAKE.map(([long, wide]) => (
          <path
            key={long}
            d={ROAD}
            strokeWidth={wide}
            strokeDasharray={`${long} ${(ROAD_LENGTH - long).toFixed(2)}`}
            style={{ "--back": `${long}px` } as CSSProperties}
            className="animate-slither"
          />
        ))}
      </g>
      <g fill="none" stroke={tile} strokeWidth={GAP} strokeLinecap="round">
        {TAILS.map((d) => (
          <path key={d} d={d} />
        ))}
      </g>
      <g fill={light}>
        {EYES.map(([cx, cy]) => (
          <circle key={`l${cx},${cy}`} cx={cx - GLANCE} cy={cy - GLANCE} r="0.8" />
        ))}
      </g>
    </svg>
  );
}
